import { useMemo } from 'react';
import { useEntityList } from '@/hooks/useEntityList';
import { useEntityOperations } from '@/hooks/common/useEntityOperations';
import { TABLE_NAMES } from '@/constants/tableNames';
import { 
  Sale,
  SaleCreate,
  SaleUpdate,
  SaleFilters,
  SaleAnalytics,
  DbSale
} from '@/types/entities/sale';
import { 
  dbSaleToSale,
  saleCreateToDbInsert,
  saleUpdateToDbUpdate,
  mapSaleItemsToDbInserts
} from '@/utils/mappers/salesMappers';
import { useAuth } from '@/context/AuthContext';

// Узкое сужение типа ответа от create/update к DbSale, чтобы избежать небезопасных присваиваний
function isDbSale(value: unknown): value is DbSale {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<DbSale> & Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.user_id === 'string' &&
    typeof v.total_amount === 'number' &&
    typeof v.paid_amount === 'number' &&
    typeof v.created_at === 'string' &&
    typeof v.updated_at === 'string'
  );
}

const TABLE_NAME = TABLE_NAMES.sales;
const QUERY_KEY = ['sales'];

export function useSalesUnified(filters?: SaleFilters) {
  const { user } = useAuth();

  // Получение списка продаж из таблицы sales
  const { 
    data: allSales, 
    isLoading, 
    error, 
    refetch 
  } = useEntityList<Sale, 'sales'>({
    tableName: 'sales',
    queryKey: [
      ...QUERY_KEY, 
      filters?.search, 
      filters?.clientId,
      filters?.paymentMethodId,
      filters?.paymentStatusId,
      filters?.minAmount?.toString(),
      filters?.maxAmount?.toString(),
      filters?.dateFrom?.toISOString(),
      filters?.dateTo?.toISOString(),
      filters?.rentalPointId
    ],
    dbEntityToEntityMapper: dbSaleToSale,
    filters: {
      user_id: user?.id,
    },
    enabled: !!user?.id,
  });

  // CRUD операции
  const { create, update, delete: deleteEntity, loading: operationsLoading } = useEntityOperations(
    TABLE_NAME,
    'Продажа'
  );

  // Клиентская фильтрация
  const sales = useMemo(() => {
    if (!allSales) return [];

    return allSales.filter(sale => {
      // Фильтрация по клиенту
      if (filters?.clientId && sale.clientId !== filters.clientId) {
        return false;
      }

      // Фильтрация по способу оплаты
      if (filters?.paymentMethodId && sale.paymentMethodId !== filters.paymentMethodId) {
        return false;
      }

      // Фильтрация по статусу оплаты
      if (filters?.paymentStatusId && sale.paymentStatusId !== filters.paymentStatusId) {
        return false;
      }

      // Фильтрация по сумме
      if (filters?.minAmount !== undefined && sale.totalAmount < filters.minAmount) {
        return false;
      }
      if (filters?.maxAmount !== undefined && sale.totalAmount > filters.maxAmount) {
        return false;
      }

      // Фильтрация по дате
      if (filters?.dateFrom && sale.createdAt < filters.dateFrom) {
        return false;
      }
      if (filters?.dateTo && sale.createdAt > filters.dateTo) {
        return false;
      }

      // Фильтрация по точке проката
      if (filters?.rentalPointId && sale.rentalPointId !== filters.rentalPointId) {
        return false;
      }

      // Поиск по примечаниям
      if (filters?.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesNotes = sale.notes?.toLowerCase().includes(searchLower);
        const matchesClient = sale.clientName?.toLowerCase().includes(searchLower);
        
        if (!matchesNotes && !matchesClient) {
          return false;
        }
      }

      return true;
    });
  }, [allSales, filters]);

  // Аналитика продаж
  const analytics = useMemo((): SaleAnalytics => {
    const totalRevenue = sales.reduce((sum, sale) => sum + sale.totalAmount, 0);
    const salesCount = sales.length;
    const averageSale = salesCount > 0 ? totalRevenue / salesCount : 0;
    
    // Группировка по способам оплаты
    const byPaymentMethod: Record<string, number> = {};
    sales.forEach(sale => {
      const method = sale.paymentMethodName || 'Неизвестно';
      byPaymentMethod[method] = (byPaymentMethod[method] || 0) + sale.totalAmount;
    });

    // Группировка по клиентам
    const byClient: Record<string, number> = {};
    sales.forEach(sale => {
      const client = sale.clientName || 'Неизвестно';
      byClient[client] = (byClient[client] || 0) + sale.totalAmount;
    });

    return {
      totalSales: salesCount,
      totalRevenue,
      averageSale,
      salesCount,
      byPaymentMethod,
      byClient,
      periodComparison: {
        percentChange: 0, // Будет вычисляться отдельно при необходимости
        previousPeriod: 0
      },
      sales
    };
  }, [sales]);

  // Создание продажи и её позиций
  const createSale = async (saleData: SaleCreate): Promise<Sale> => {
    if (!user?.id) {
      throw new Error('Пользователь не авторизован');
    }

    const dbData = saleCreateToDbInsert(saleData, user.id);
    const resultUnknown: unknown = await create(dbData as unknown as Record<string, unknown>);
    if (!isDbSale(resultUnknown)) {
      throw new Error('Unexpected response from create(sales)');
    }
    const createdSale = dbSaleToSale(resultUnknown);

    // Batch insert sale items if provided
    const itemsPayload = mapSaleItemsToDbInserts(createdSale.id, saleData.items);
    if (itemsPayload.length > 0) {
      // Use direct Supabase call to sale_items to avoid coupling generic ops with batching
      const { supabaseWorker } = await import('@/integrations/supabase/workerClient');
      const { error: itemsError } = await supabaseWorker
        .from(TABLE_NAMES.sale_items)
        .insert(itemsPayload);
      if (itemsError) throw itemsError;

      // After items are inserted, decrement store product stock/quantity accordingly
      // Aggregate quantities by store_product_id to minimize round-trips
      const productQtyMap = new Map<string, number>();
      for (const it of itemsPayload) {
        if (it.store_product_id) {
          const current = productQtyMap.get(it.store_product_id) || 0;
          productQtyMap.set(it.store_product_id, current + (it.quantity || 0));
        }
      }

      // Update each affected product: quantity and legacy stock fields, not below zero
      for (const [productId, soldQty] of productQtyMap.entries()) {
        // Fetch current values
        const { data: prod, error: fetchErr } = await supabaseWorker
          .from('store_products')
          .select('id, quantity, stock')
          .eq('id', productId)
          .single();
        if (fetchErr) {
          // If we cannot fetch, continue to next product but log server-side
          const msg = fetchErr instanceof Error ? fetchErr.message : String(fetchErr);
          console.warn('[useSalesUnified] Failed to fetch store product for stock update', { productId, error: msg });
          continue;
        }
        type StoreProductRow = { id: string; quantity: number | null; stock: number | null };
        const prodRow = (prod as unknown) as Partial<StoreProductRow> | null;
        const currentQty = Math.max(0, Number(prodRow?.quantity ?? 0));
        const currentStock = Math.max(0, Number(prodRow?.stock ?? prodRow?.quantity ?? 0));
        const nextQty = Math.max(0, currentQty - soldQty);
        const nextStock = Math.max(0, currentStock - soldQty);

        const { error: updErr } = await supabaseWorker
          .from('store_products')
          .update({ quantity: nextQty, stock: nextStock })
          .eq('id', productId);
        if (updErr) {
          const msg = updErr instanceof Error ? updErr.message : String(updErr);
          console.warn('[useSalesUnified] Failed to update store product stock', { productId, error: msg });
        }
      }
    }

    return createdSale;
  };

  // Обновление продажи
  const updateSale = async (saleData: SaleUpdate): Promise<Sale> => {
    const { id } = saleData;
    const dbUpdateData = saleUpdateToDbUpdate(saleData);
    
    const resultUnknown: unknown = await update(id, dbUpdateData as unknown as Record<string, unknown>);
    if (!isDbSale(resultUnknown)) {
      throw new Error('Unexpected response from update(sales)');
    }
    return dbSaleToSale(resultUnknown);
  };

  // Удаление продажи
  const deleteSale = async (id: string): Promise<void> => {
    await deleteEntity(id);
  };

  return {
    // Данные
    sales,
    allSales,
    analytics,
    
    // Состояние загрузки
    isLoading,
    error,
    
    // Операции
    createSale,
    updateSale,
    deleteSale,
    refetch,
    
    // Состояние операций
    isCreating: operationsLoading,
    isUpdating: operationsLoading,
    isDeleting: operationsLoading,
  };
}
